import React, { useEffect } from 'react';
import NewsletterSignup from '../components/NewsletterSignup';

const FEATURED = {
  date: 'Week of Aug 24, 2026',
  gradeBands: ['Grades 9–10', 'Grades 11–12'],
  title: 'Audit the activity list before school starts, not in October.',
  paragraphs: [
    'Sit down with your student this week and rank every activity by one question: if we had to drop it, what verifiable result would we lose? Anything without an answer — no ranking, no shipped project, no role with responsibility — is a candidate to cut before the semester locks the schedule in.',
    'The pattern in our verified case library is consistent: admits average one or two deep, checkable commitments, and the time for depth has to come from somewhere. Cutting the third club in August feels harsh; cutting it in January, after it has already eaten a season of evenings, is the version families regret.',
  ],
};

const ARCHIVE = [
  {
    date: 'Week of Aug 17',
    gradeBands: ['Grades 6–8'],
    title: 'Middle school is for sampling, not specializing — with one exception.',
    blurb:
      'Why breadth wins before 9th grade, and the one case (math and music) where early depth actually compounds.',
  },
  {
    date: 'Week of Aug 10',
    gradeBands: ['Grades 11–12'],
    title: 'The recommendation-letter ask that gets a better letter.',
    blurb:
      'What to hand a teacher in September — a one-page brag sheet with specifics they can quote — and why the timing matters more than the choice of teacher.',
  },
  {
    date: 'Week of Aug 3',
    gradeBands: ['Grades 9–10'],
    title: 'Free beats paid for a first research experience.',
    blurb:
      'The cold-email template that got three of our mentors into university labs, and the paid-program red flags from the parent cost records.',
  },
  {
    date: 'Week of Jul 27',
    gradeBands: ['All grades'],
    title: 'Track hours per activity for one normal week.',
    blurb:
      'Most families are off by 2x when they estimate. One honest week of data changes the conversation about what to keep.',
  },
  {
    date: 'Week of Jul 20',
    gradeBands: ['Grades 11–12'],
    title: 'Summer results expire — bank the evidence now.',
    blurb:
      'Screenshots, links, rankings, and letters gathered in July take ten minutes; reconstructing them for the Common App in October takes weeks.',
  },
];

const WeeklyTipsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Weekly tips · free
          </span>
          <h1 style={{ maxWidth: '24ch' }}>One concrete thing to do this week.</h1>
          <p style={{ color: 'var(--ink-secondary)', marginTop: 16, maxWidth: '62ch' }}>
            Every week, one specific, doable action drawn from mentor sessions and our verified
            data — tagged by grade band so you know if it applies to your family yet. No theory,
            no “start earlier” guilt.
          </p>
        </div>
      </header>

      {/* This week */}
      <section className="section warm" style={{ paddingTop: 48 }}>
        <div className="wrap" style={{ maxWidth: 880 }}>
          <div className="section-head">
            <span className="eyebrow">This week</span>
          </div>
          <div className="card" style={{ padding: 36 }}>
            <div className="meta" style={{ marginBottom: 10 }}>{FEATURED.date}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              {FEATURED.gradeBands.map((band) => (
                <span key={band} className="tag solid">{band}</span>
              ))}
            </div>
            <h2 className="serif" style={{ fontSize: 26, marginBottom: 14 }}>{FEATURED.title}</h2>
            {FEATURED.paragraphs.map((paragraph, i) => (
              <p
                key={i}
                style={{ color: 'var(--ink-secondary)', fontSize: 15, lineHeight: 1.7, marginBottom: 12 }}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Archive */}
      <section className="section">
        <div className="wrap" style={{ maxWidth: 880 }}>
          <div className="section-head">
            <span className="eyebrow">Previous weeks</span>
            <h2>The archive.</h2>
            <p>Full write-ups for past tips go out with the weekly email.</p>
          </div>
          {ARCHIVE.map((tip) => (
            <div key={tip.title} className="list-row" style={{ alignItems: 'flex-start' }}>
              <div>
                <div className="meta" style={{ marginBottom: 6 }}>{tip.date}</div>
                <h3 style={{ marginBottom: 6 }}>{tip.title}</h3>
                <p className="meta" style={{ fontSize: 14.5, lineHeight: 1.6 }}>{tip.blurb}</p>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 'none' }}>
                {tip.gradeBands.map((band) => (
                  <span key={band} className="tag solid">{band}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <NewsletterSignup
        variant="band"
        source="weekly-tips"
        heading="Get next week’s tip in your inbox."
        subtext="One concrete action a week, tagged by grade band — free, no spam, unsubscribe anytime."
      />
    </>
  );
};

export default WeeklyTipsPage;
